/**
 * Compact card for the Account page that lets the user verify their email.
 *
 * Mirrors the phone-OTP UX:
 *   1. User clicks "Send code" → POST /api/auth/email-otp/request/
 *   2. We open a modal asking for the 6-digit code
 *   3. User submits → POST /api/auth/email-otp/verify/
 *   4. Backend flips `email_verified_at`; we refetch /profile/me/
 *
 * The card hides itself once `email_verified_at` is set.
 */

import { useState } from "react";
import styled from "styled-components";
import { Button, Input, Modal, message } from "antd";
import { MailOutline } from "@raresail/react-ionicons";
import { useAuth } from "../../context/AuthContext";
import useSet from "../../../hooks/common/useSet";
import useGet from "../../../hooks/common/useGet";
import {
  API_EMAIL_OTP_REQUEST,
  API_EMAIL_OTP_VERIFY,
  API_PROFILE_ME,
} from "../../../constants/apis";
import { PRIMARY_COLOR, primary10 } from "../../../constants/colors";

export const EmailVerificationCard = () => {
  const { user, updateUser } = useAuth();
  const isVerified = !!user?.email_verified_at;

  const [modalOpen, setModalOpen] = useState(false);
  const [code, setCode] = useState("");

  const { uploadData: requestOtp, submitting: requesting } = useSet();
  const { uploadData: verifyOtp, submitting: verifying } = useSet();
  const { loadData: refreshMe } = useGet();

  // Hide once verified — nothing for the user to do.
  if (isVerified) return null;

  const requestCode = (openModalAfter: boolean) => {
    requestOtp({
      api: API_EMAIL_OTP_REQUEST,
      params: {},
      method: "POST",
      successMessage: openModalAfter
        ? { message: "Code sent to your email", duration: 4 }
        : undefined,
      onSuccess: () => {
        if (openModalAfter) setModalOpen(true);
      },
    });
  };

  const submit = () => {
    if (!code || code.trim().length < 4) {
      message.error("Enter the code from your email.");
      return;
    }
    verifyOtp({
      api: API_EMAIL_OTP_VERIFY,
      params: { code: code.trim() },
      method: "POST",
      successMessage: { message: "Email verified!", duration: 3 },
      onSuccess: () => {
        setModalOpen(false);
        setCode("");
        // Refresh /profile/me/ so the navbar + this card update
        refreshMe({
          api: API_PROFILE_ME,
          onSuccess: (me: any) => updateUser(me),
        });
      },
    });
  };

  return (
    <>
      <Card>
        <IconBubble>
          <MailOutline fontSize={20} color={PRIMARY_COLOR} />
        </IconBubble>
        <div className="flexGrow">
          <div className="font14 semiBold textDark">Verify your email</div>
          <div className="font12 grey" style={{ lineHeight: 1.4 }}>
            We'll send a 6-digit code to{" "}
            <b style={{ color: "#0f172a" }}>{user?.email}</b> so we know it's
            really yours.
          </div>
        </div>
        <Button type="primary" loading={requesting} onClick={() => requestCode(true)}>
          Send code
        </Button>
      </Card>

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title="Enter verification code"
        footer={null}
        width={400}
      >
        <p className="font13 grey" style={{ marginTop: 0 }}>
          We sent a 6-digit code to <b>{user?.email}</b>. It expires in a few
          minutes.
        </p>
        <Input
          autoFocus
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="123456"
          style={{
            fontSize: 22,
            letterSpacing: 6,
            textAlign: "center",
            fontWeight: 700,
          }}
        />
        <div
          className="flexNullCenter"
          style={{ marginTop: 16, justifyContent: "space-between" }}
        >
          <Button type="link" onClick={() => requestCode(false)} disabled={requesting}>
            Resend code
          </Button>
          <Button
            type="primary"
            loading={verifying}
            onClick={submit}
            disabled={code.length < 4}
          >
            Verify
          </Button>
        </div>
      </Modal>
    </>
  );
};

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid ${primary10};
  border-radius: 12px;
`;

const IconBubble = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${primary10};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;
